import React, { MouseEvent, useEffect, useRef, useState } from "react";
import { ChevronDown, Download } from "lucide-react";
import { OptionNameValuePair } from "../../../../lib/interfaces/types";

export interface MenuDropdownButtonProps<T = string> {
  label: string;
  options: OptionNameValuePair<T>[];
  onSelect: (value: T) => void;
}

export const MenuDropdownButton = <T extends string = string>({
  label,
  options,
  onSelect,
}: MenuDropdownButtonProps<T>) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const toggle = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={toggle}
        className="flex h-9 items-center justify-center gap-2 rounded-md border border-[#3551F3] bg-white px-4 py-2 text-sm font-medium text-[#3551F3] whitespace-nowrap transition-opacity hover:opacity-80 cursor-pointer"
      >
        <Download size={16} strokeWidth={2} />
        {label}
        <ChevronDown size={16} strokeWidth={2} />
      </button>

      {open && (
        <div className="absolute right-0 z-[9999] mt-1 min-w-[200px] overflow-hidden rounded-md border border-[#E5E5E5] bg-white py-1 shadow-md">
          {options.map((option) => (
            <button
              key={option.name}
              type="button"
              disabled={option.disabled}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                setOpen(false);
                onSelect(option.value);
              }}
              className="flex w-full items-center px-3 py-2 text-left text-sm text-[#171717] hover:bg-[#F5F5F4] cursor-pointer"
            >
              {option.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MenuDropdownButton;